//#region Imports
import {
  allModels,
  ioViewer,
  ioPlane,
  ioRaycaster,
  viewMode,
  updateRaycasterFromEvent,
  unfocusAny,
  rotateIntraoralToView,
  upper_measurements,
  lower_measurements,
  upperBox,
  lowerBox,
  resetIoView,
  resetIoCamera,
  defaultIoBox,
  sceneBackgroundColor,
  switchViewModes,
} from "../IntraoralManager";
import {
  drawBox,
  drawLine,
  drawPoint,
  getDistance,
  getDistance2D,
} from "../IntraoralDraw";
import IntraoralArchControls from "./IntraoralArchControls.vue";
import IntraoralArchPanel from "./IntraoralArchPanel.vue";

import { DynamicTubeGeometry } from "../../geometry/DynamicTubeGeometry";
import { MeshLine, MeshLineMaterial, MeshLineRaycast } from "three.meshline";
import { initToothNumberOptions } from "../number/IntraoralNumberMode";
import {
  enterMeasureMode,
  initMeasureMeshes,
  molarLines,
} from "./IntraoralMeasureMode";
import { showLoader, hideLoader } from "@/components/controls/Loader.vue";
import {
  saveMeasurements,
  sendCreateMeasurementsRequest,
} from "@/api/intraoralApi";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import { getFormattedDate, saveFile } from "@/components/reports/fileMethods";
import { degreesToRadians } from "@/components/geometry/geometry.js";
import { getImgDataForCurrentView } from "@/components/reports/ReportsManager";
import html2canvas from "html2canvas";

//#endregion

//#region Fields
const CONTROL_TAG = "archControl";
const YELLOW_COLOR = new THREE.Color("#FFFF00");
const MEASURE_COLOR_HEX = "#FFFF00";
const MEASURE_COLOR = new THREE.Color(MEASURE_COLOR_HEX);
const POINT_COLOR = new THREE.Color("#00FF00");
const MIDDLE_COLOR = new THREE.Color("#FF0000");

const editContext = {
  isActive: false,
  distance: 0,
  isWhite: false,
  whiteColor: "#ffffff",
  blueColor: "#007bff",
  symmPoints: true,
  showLines: false,
};

const commonContext = {
  hasUpper: false,
  upperTeethTwelveSum: 0,
  upperTeethFirstSixSum: 0,
  upperArchFirstSixSum: 0,
  upperTeethPremolarSum: 0,
  upperArchPremolarSum: 0,
  upperTeethAllSum: 0,
  upperArchAllSum: 0,

  hasLower: false,
  lowerTeethTwelveSum: 0,
  lowerTeethFirstSixSum: 0,
  lowerArchFirstSixSum: 0,
  lowerTeethPremolarSum: 0,
  lowerArchPremolarSum: 0,
  lowerTeethAllSum: 0,
  lowerArchAllSum: 0,

  lowerStart6: null,
  lowerEnd6: null,
  upperStart6: null,
  upperEnd6: null,

  getUpperFirstSixDeltaText() {
    let delta = this.upperTeethFirstSixSum - this.upperArchFirstSixSum;
    let comment = delta <= 0 ? "spacing" : "crowding";
    return `${Math.abs(delta).toFixed(2)} (${comment})`;
  },
  getUpperPremolarDeltaText() {
    let delta = this.upperTeethPremolarSum - this.upperArchPremolarSum;
    let comment = delta <= 0 ? "spacing" : "crowding";
    return `${Math.abs(delta).toFixed(2)} (${comment})`;
  },
  getUpperAllDeltaText() {
    let delta = this.upperTeethAllSum - this.upperArchAllSum;
    let comment = delta <= 0 ? "spacing" : "crowding";
    return `${Math.abs(delta).toFixed(2)} (${comment})`;
  },

  getLowerFirstSixDeltaText() {
    let delta = this.lowerTeethFirstSixSum - this.lowerArchFirstSixSum;
    let comment = delta <= 0 ? "spacing" : "crowding";
    return `${Math.abs(delta).toFixed(2)} (${comment})`;
  },
  getLowerPremolarDeltaText() {
    let delta = this.lowerTeethPremolarSum - this.lowerArchPremolarSum;
    let comment = delta <= 0 ? "spacing" : "crowding";
    return `${Math.abs(delta).toFixed(2)} (${comment})`;
  },
  getLowerAllDeltaText() {
    let delta = this.lowerTeethAllSum - this.lowerArchAllSum;
    let comment = delta <= 0 ? "spacing" : "crowding";
    return `${Math.abs(delta).toFixed(2)} (${comment})`;
  },

  getFirstSixRatioPercent() {
    return +(
      (this.lowerTeethFirstSixSum * 100) /
      this.upperTeethFirstSixSum
    ).toFixed(2);
  },
  getFirstSixBoltonAnalysis() {
    let ratioPercent = this.getFirstSixRatioPercent();
    return ratioPercent < 74 || ratioPercent > 81
      ? "Occlusal Disproportion"
      : "Occlusal Normal";
  },
  getFirstTwelveRatioPercent() {
    return +(
      (this.lowerTeethTwelveSum * 100) /
      this.upperTeethTwelveSum
    ).toFixed(2);
  },
  getFirstTwelveBoltonAnalysis() {
    let ratioPercent = this.getFirstTwelveRatioPercent();
    return ratioPercent < 88 || ratioPercent > 94
      ? "Occlusal Disproportion"
      : "Occlusal Normal";
  },
};

let reportZoom = 0;

let controlsInitialized = false;
let autoTableInitialised = false;
let upperArchMesh = null;
let lowerArchMesh = null;

let firstClick = true;
let clickTime = Date.now();
let dragPoint = null;
let dragPos = null;
let clickDif = 300;

let tagPos = null;
let tagControl = null;

let ioArchMenu = null;
let ioArchAddBtn = null;
let ioArchMeasureBtn = null;
let ioArchRemoveBtn = null;
let newArchPoint = null;
let clickedPoint = null;

let archPlane = new THREE.Mesh(
  new THREE.PlaneBufferGeometry(500, 500, 8, 8),
  new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    opacity: 0.00001, //0.00001, 0.5
  }),
);
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

//#endregion

async function switchArchMode(isArch) {
  console.log("switchArchMode", isArch);
  if (isArch) {
    await enterArchMode(true);
  } else if (viewMode.isArchMode) {
    viewMode.isArchMode = editContext.isActive = isArch;
    leaveArchMode();
  }
}

async function enterArchMode(showControls) {
  let measurements = viewMode.isUpper ? upper_measurements : lower_measurements;
  if (!measurements) {
    if (viewMode.isPostprocessing) {
      swal({
        text: "Please wait for case postprocessing to complete.",
      });
    } else {
      swal({
        text: "Teeth measurements not found. Would you like to generate them?",
        buttons: {
          yes: { text: "Yes", value: true },
          no: { text: "No", value: false },
        },
      }).then(async (postprocess) => {
        if (postprocess) {
          viewMode.isPostprocessing = true;
          sendCreateMeasurementsRequest();
        }
      });
    }
    return false;
  }

  viewMode.isArchMode = editContext.isActive = true;

  await initToothNumberOptions();
  initMeasureMeshes();

  initializeIoArch();
  updateFirsSixArch();
  await unfocusAny();

  rotateIntraoralToView(2);
  await delay(100);

  viewMode.isArchMode = true;
  editContext.isWhite = false; //vueApp.isWhite;
  editContext.whiteColor = vueApp.whiteColor;
  editContext.blueColor = vueApp.blueColor;

  if (upperArchMesh) {
    upperArchMesh.visible = viewMode.isUpper;
    if (viewMode.isUpper) {
      archPlane.position.set(
        upperArchMesh.box.center.x,
        upperArchMesh.box.center.y,
        upperArchMesh.box.center.z,
      );
    }
    drawArchLabel(upperArchMesh.labels[0], `${upperArchMesh.totalDistance} mm`);
    drawArchLabel(
      upperArchMesh.labels[1],
      `${upperArchMesh.premolarDistance} mm`,
    );
    // drawArchLabel(
    //   upperArchMesh.labels[2],
    //   `${upperArchMesh.firstSixDistance} mm`,
    // );

    for (const label of upperArchMesh.labels) {
      label.style.display =
        upperArchMesh.visible && label.visible ? "block" : "none";
    }
  }
  if (lowerArchMesh) {
    lowerArchMesh.visible = !viewMode.isUpper;
    if (!viewMode.isUpper) {
      archPlane.position.set(
        lowerArchMesh.box.center.x,
        lowerArchMesh.box.center.y,
        lowerArchMesh.box.center.z,
      );
    }
    drawArchLabel(lowerArchMesh.labels[0], `${lowerArchMesh.totalDistance} mm`);
    drawArchLabel(
      lowerArchMesh.labels[1],
      `${lowerArchMesh.premolarDistance} mm`,
    );
    // drawArchLabel(
    //   lowerArchMesh.labels[2],
    //   `${lowerArchMesh.firstSixDistance} mm`,
    // );
    for (const label of lowerArchMesh.labels) {
      label.style.display =
        lowerArchMesh.visible && label.visible ? "block" : "none";
    }
  }
  archPlane.lookAt(new THREE.Vector3(0, 0, 1000)); //ioViewer.camera.position);

  if (showControls) {
    document.getElementById("ioArchControls").style.display = "block";
    document.getElementById("ioArchPanel").style.display = "block";

    $(`#intraoralArchPanel`).addClass("show");
  }

  switchShowLines(editContext.showLines);
}

async function leaveArchMode(saveChanges) {
  editContext.distance = 0;

  let controls = document.getElementById("ioArchControls");
  if (controls) controls.style.display = "none";
  controls = document.getElementById("ioArchPanel");
  if (controls) controls.style.display = "none";

  if (upperArchMesh) {
    upperArchMesh.visible = false;
    for (const label of upperArchMesh.labels) {
      label.style.display = "none";
    }

    updateJsonFromArchMesh(upperArchMesh, upper_measurements);
  }

  if (lowerArchMesh) {
    lowerArchMesh.visible = false;
    for (const label of lowerArchMesh.labels) {
      label.style.display = "none";
    }
    updateJsonFromArchMesh(lowerArchMesh, lower_measurements);
  }

  for (const model of allModels
    .filter((m) => m.measurePoints)
    .sort((a, b) => b.number - a.number)) {
    if (model.measureMeshes) {
      for (const mesh of model.measureMeshes) {
        mesh.visible = false;
      }
    }
  }

  if (saveChanges) {
    showLoader(`Saving data... Please wait`, "", true, 0.7);
    let result = true;
    if (upper_measurements) await saveMeasurements(upper_measurements, true);
    if (lower_measurements)
      result = result && (await saveMeasurements(lower_measurements, false));
    hideLoader();
    swal(result ? "Measurements saved" : "Failed to save measurements");
  }

  viewMode.isArchMode = false;
}

function updateJsonFromArchMesh(archMesh, jsonObj) {
  jsonObj.arch_curve_prj_params = [];
  for (const point of archMesh.mainPoints) {
    let arr = [point.x, point.y];
    if (point.isAdded) arr.push(true);
    jsonObj.arch_curve_prj_params.push(arr);
  }
  jsonObj.arch_curve_all_points = [];
  for (const point of archMesh.allPoints) {
    jsonObj.arch_curve_all_points.push([point.x, point.y]);
  }
  jsonObj.arch_total_distance = +archMesh.totalDistance;
  jsonObj.arch_premolar_distance = +archMesh.premolarDistance;
  jsonObj.arch_first_six_distance = +archMesh.firstSixDistance;
  jsonObj.arch_offset = [archMesh.position.x, archMesh.position.y];
  jsonObj.arch_rotation = archMesh.rotation.z;
  jsonObj.arch_control_point = [
    archMesh.controlPoint.x,
    archMesh.controlPoint.y,
  ];
}

function initializeIoArch() {
  if (!controlsInitialized) {
    const ctrls = [IntraoralArchControls, IntraoralArchPanel];
    ctrls.forEach((component) => {
      const componentInstance = new Vue({
        ...component,
        propsData: {
          editContext: editContext,
          commonContext: commonContext,
        },
      });
      const vueContainer = document.createElement("div");
      document.getElementById("intraoralViewer").appendChild(vueContainer);
      componentInstance.$mount(vueContainer);
    });

    ioViewer.scene.add(archPlane);

    if (upper_measurements) {
      upperArchMesh = createArchMesh(upper_measurements, true);
      if (upperArchMesh) {
        commonContext.hasUpper = true;
        commonContext.upperArchAllSum = +upperArchMesh.totalDistance;
        commonContext.upperArchPremolarSum = +upperArchMesh.premolarDistance;
        // commonContext.upperArchFirstSixSum = +upperArchMesh.firstSixDistance;
      }
    }

    if (lower_measurements) {
      lowerArchMesh = createArchMesh(lower_measurements, false);
      if (lowerArchMesh) {
        commonContext.hasLower = true;
        commonContext.lowerArchAllSum = +lowerArchMesh.totalDistance;
        commonContext.lowerArchPremolarSum = +lowerArchMesh.premolarDistance;
        // commonContext.lowerArchFirstSixSum = +lowerArchMesh.firstSixDistance;
      }
    }
    ioViewer.controls.addEventListener("change", updateArcLabels);

    initArchMenu();

    controlsInitialized = true;
  }
}

function initArchMenu() {
  ioArchMenu = document.getElementById("ioarch-menu");
  document.getElementById("intraoralViewer").appendChild(ioArchMenu);

  ioArchAddBtn = document.getElementById("ioarch-add-btn");
  ioArchAddBtn.addEventListener("click", () => {
    console.log("ioarch add click");
    ioArchMenu.style.display = "none";

    if (newArchPoint) {
      let archMesh = viewMode.isUpper ? upperArchMesh : lowerArchMesh;
      let newPoint = new THREE.Vector3(
        newArchPoint.x - archMesh.position.x,
        newArchPoint.y - archMesh.position.y,
        archMesh.centerZ,
      );
      newPoint = getNearestPoint(archMesh.allPoints, newPoint);
      newPoint.isAdded = true;
      let mainPoints = archMesh.mainPoints;
      let mainPoint1 = getNearestPoint(mainPoints, newPoint);
      let mainPoint2 = getNearestPoint(
        mainPoints.filter((p) => p != mainPoint1),
        newPoint,
      );

      let newIndex = Math.max(
        mainPoints.indexOf(mainPoint1),
        mainPoints.indexOf(mainPoint2),
      );
      mainPoints.splice(newIndex, 0, newPoint);

      let pointMesh = drawArchPoint(newPoint, archMesh, 0x00ff00);
      pointMesh.index = newIndex;
      archMesh.pointMeshes.splice(newIndex, 0, pointMesh);
      // drawPoint(newArchPoint, ioViewer.scene, 0x00ff00);
      ioArchMenu.style.display = "none";

      updateArchAndDistances(archMesh);
    }
  });

  ioArchRemoveBtn = document.getElementById("ioarch-remove-btn");
  ioArchRemoveBtn.addEventListener("click", () => {
    console.log("ioarch remove click");
    ioArchMenu.style.display = "none";

    if (clickedPoint && clickedPoint.isAdded) {
      let archMesh = viewMode.isUpper ? upperArchMesh : lowerArchMesh;
      archMesh.remove(clickedPoint);
      const index = archMesh.pointMeshes.findIndex((m) => m === clickedPoint);
      if (index !== -1) {
        archMesh.pointMeshes.splice(index, 1);
        archMesh.mainPoints.splice(index, 1);
        updateArchAndDistances(archMesh);
      }
    }
  });

  ioArchMeasureBtn = document.getElementById("ioarch-measure-btn");
  ioArchMeasureBtn.addEventListener("click", async () => {
    console.log("ioarch measure click");
    ioArchMenu.style.display = "none";
    if (clickedPoint) {
      let archMesh = viewMode.isUpper ? upperArchMesh : lowerArchMesh;

      let selectedPoints = archMesh.selectedPoints;
      if (!selectedPoints[0]) {
        selectedPoints[0] = clickedPoint;
      } else {
        selectedPoints[1] = selectedPoints[0];
        selectedPoints[0] = clickedPoint;
      }

      updateSelectedPoints();
    }
  });
}

//#region Create meshes

function createArchMesh(jsonObj, isUpper) {
  console.log("createArchMesh", jsonObj);
  let teetBox = isUpper ? upperBox : lowerBox;
  let tooth1 = isUpper
    ? allModels.find((x) => +x.number == 11)
    : allModels.find((x) => +x.number == 31);
  let tooth2 = isUpper
    ? allModels.find((x) => +x.number == 21)
    : allModels.find((x) => +x.number == 41);

  let basePoints = [];
  let mainPoints = [];
  for (const arr of jsonObj.arch_curve_prj_params) {
    let mainPoint = new THREE.Vector3(arr[0], arr[1], teetBox.max.z);
    mainPoints.push(mainPoint);
    if (!arr[2]) {
      basePoints.push(mainPoint);
    } else {
      mainPoint.isAdded = true;
    }
  }
  // let firstPoint = basePoints[0];
  // let lastPoint = basePoints[basePoints.length - 1];
  // let bottomY = Math.min(firstPoint.y, lastPoint.y);

  let allPoints = getSplinePoints(mainPoints, teetBox.max.z);
  let totalDistance = allPoints.reduce((acc, curr, i, arr) => {
    return i === 0 ? acc : acc + getDistance(arr[i - 1], arr[i]);
  }, 0);

  let archMesh = drawArchCurve(allPoints);
  archMesh.mainPoints = mainPoints;
  archMesh.jsonObj = jsonObj;
  archMesh.selectedPoints = [];
  archMesh.allPoints = allPoints;
  archMesh.totalDistance = totalDistance.toFixed(2);

  updatePremolarDistance(archMesh);
  // updateFirstSixDistance(archMesh);
  archMesh.geometry.computeBoundingBox();
  let archBox = (archMesh.box = offsetBox(archMesh.geometry.boundingBox));
  let centerX = archBox.center.x;
  let minY = archBox.min.y;
  let maxY = archBox.max.y;
  let centerZ = (archMesh.centerZ = archBox.center.z);

  let centerPoint = basePoints[3];
  if (tooth1 && tooth2 && tooth1.mainMesh && tooth2.mainMesh) {
    let temp = getCenterPointFromMeshes(tooth1.mainMesh, tooth2.mainMesh);
    if (temp) centerPoint = temp;
  }
  centerPoint.y = minY;
  centerPoint.z = centerZ;
  centerX = centerPoint.x;

  let pointMeshes = [];
  let baseMeshes = [];

  for (const point of mainPoints) {
    let i = basePoints.indexOf(point);
    let pointMesh = drawArchPoint(
      point,
      archMesh,
      i == 1 || i == 5 ? MIDDLE_COLOR : POINT_COLOR,
    );
    pointMeshes.push(pointMesh);

    if (i >= 0) {
      baseMeshes.push(pointMesh);
    }
  }
  archMesh.pointMeshes = pointMeshes;

  for (const [i, pointMesh] of baseMeshes.entries()) {
    if (i != 3) {
      let opposite = baseMeshes[6 - i];
      if (opposite) {
        pointMesh.opposite = opposite;
      }
      archMesh.add(drawArchLine(pointMesh, centerX));
    }
  }

  archMesh.add(
    drawMidLine(centerPoint, new THREE.Vector3(centerX, maxY, centerZ)),
  );

  let controlPoint = new THREE.Vector3(centerX, teetBox.center.y, centerZ);
  if (jsonObj.arch_control_point) {
    let arr = jsonObj.arch_control_point;
    controlPoint = new THREE.Vector3(arr[0], arr[1], centerZ);
  }

  archMesh.controlPoint = controlPoint;
  drawMoveControl(controlPoint, archMesh);
  drawRotateControl(controlPoint, archMesh);

  if (jsonObj.arch_offset) {
    archMesh.position.setX(jsonObj.arch_offset[0]);
    archMesh.position.setY(jsonObj.arch_offset[1]);
  }

  if (jsonObj.arch_rotation) {
    archMesh.rotation.set(0, 0, jsonObj.arch_rotation, "XYZ");
  }

  // let models = allModels
  //   .filter((m) => m.isTooth && m.isUpper == isUpper)
  //   .sort((a, b) => a.number - b.number);
  // let model = models[models.length - 1];
  let point = archMesh.mainPoints[archMesh.mainPoints.length - 1];
  if (point.x > archMesh.mainPoints[0].x) point = archMesh.mainPoints[0];
  let text = `${archMesh.totalDistance} mm`;
  let color = "#F1C40F"; //"#007bff";
  let totalLabel = drawArchLabel(null, text, point, color, archMesh);
  totalLabel.visible = true;

  point = archMesh.mainPoints[archMesh.mainPoints.length - 2];
  if (point.x > archMesh.mainPoints[1].x) point = archMesh.mainPoints[1];
  text = `${archMesh.premolarDistance} mm`;
  let premolarLabel = drawArchLabel(null, text, point, color, archMesh);
  premolarLabel.visible = true;

  point = archMesh.mainPoints[archMesh.mainPoints.length - 3];
  if (point.x > archMesh.mainPoints[2].x) point = archMesh.mainPoints[2];
  text = `${archMesh.firstSixDistance} mm`;
  // let firstSixLabel = drawArchLabel(null, text, point, color, archMesh);
  // firstSixLabel.visible = true;

  archMesh.selectedDistance = 0;
  text = `${archMesh.selectedDistance} mm`;
  let selectedLabel = drawArchLabel(
    null,
    text,
    point,
    MEASURE_COLOR_HEX,
    archMesh,
  );
  selectedLabel.visible = false;
  archMesh.labels = [totalLabel, premolarLabel, premolarLabel, selectedLabel];

  updateFirsSixArch();

  return archMesh;

  //#region Plane
  let arr = jsonObj.arch_plane.origin;
  let origin = new THREE.Vector3(arr[0], arr[1], arr[2]);
  arr = jsonObj.arch_plane.normal;
  let normal = new THREE.Vector3(arr[0], arr[1], arr[2]);

  let normalPlane = new THREE.Mesh(
    new THREE.PlaneBufferGeometry(100, 100, 8, 8),
    new THREE.MeshBasicMaterial({
      color: 0xff0000,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      opacity: 0.5, //0.00001, 0.5
    }),
  );
  normalPlane.position.copy(origin);
  const defaultNormal = new THREE.Vector3(0, 0, 1);
  const quaternion = new THREE.Quaternion().setFromUnitVectors(
    defaultNormal,
    normal.clone().normalize(),
  );
  normalPlane.quaternion.copy(quaternion);
  archMesh.add(normalPlane);
  return archMesh;
  //#endregion
}

function updatePremolarDistance(archMesh) {
  let mainPoints = archMesh.mainPoints;
  let allPoints = archMesh.allPoints;
  let preFirstPoint = getNearestPoint(allPoints, mainPoints[1]);
  let preLastPoint = getNearestPoint(
    allPoints,
    mainPoints[mainPoints.length - 2],
  );
  let preFirstIndex = allPoints.indexOf(preFirstPoint);
  let preLastIndex = allPoints.indexOf(preLastPoint);
  let tempPoints = allPoints.slice(preFirstIndex, preLastIndex);
  let tempDistance = tempPoints.reduce((acc, curr, i, arr) => {
    return i === 0 ? acc : acc + getDistance(arr[i - 1], arr[i]);
  }, 0);
  archMesh.premolarDistance = tempDistance.toFixed(2);
}

function updateFirstSixDistance(archMesh) {
  let mainPoints = archMesh.mainPoints;
  let allPoints = archMesh.allPoints;
  let preFirstPoint = getNearestPoint(allPoints, mainPoints[2]);
  let preLastPoint = getNearestPoint(
    allPoints,
    mainPoints[mainPoints.length - 3],
  );
  let preFirstIndex = allPoints.indexOf(preFirstPoint);
  let preLastIndex = allPoints.indexOf(preLastPoint);
  let tempPoints = allPoints.slice(preFirstIndex, preLastIndex);
  let tempDistance = tempPoints.reduce((acc, curr, i, arr) => {
    return i === 0 ? acc : acc + getDistance(arr[i - 1], arr[i]);
  }, 0);
  archMesh.firstSixDistance = tempDistance.toFixed(2);
}

function updateSelectedDistance(archMesh) {
  archMesh.selectedDistance = 0;
  let selectedPoints = archMesh.selectedPoints.map((p) => p.current);
  selectedPoints = selectedPoints.filter((p) => p);
  if (selectedPoints.length < 2) {
    return;
  }
  let allPoints = archMesh.allPoints;
  let preFirstPoint = getNearestPoint(allPoints, selectedPoints[0]);
  let preLastPoint = getNearestPoint(allPoints, selectedPoints[1]);
  let preFirstIndex = allPoints.indexOf(preFirstPoint);
  let preLastIndex = allPoints.indexOf(preLastPoint);

  let firstIndex = Math.min(preFirstIndex, preLastIndex);
  let lastIndex = Math.max(preFirstIndex, preLastIndex);

  let points = allPoints.slice(firstIndex, lastIndex);
  let distance = points.reduce((acc, curr, i, arr) => {
    return i === 0 ? acc : acc + getDistance(arr[i - 1], arr[i]);
  }, 0);
  archMesh.selectedDistance = distance.toFixed(2);
}

function drawMoveControl(point, archMesh, color) {
  if (point) {
    let moveControl = null;

    if (!color) {
      color = 0xff0000;
    }
    color = new THREE.Color(color);
    const geometry = new THREE.BoxGeometry(2, 2, 0.8);
    const material = new THREE.MeshBasicMaterial({
      color: color,
      transparent: true,
      side: 2,
      // depthTest: false,
      // depthWrite: false,
      polygonOffset: true,
      opacity: 0.5,
    });
    material.polygonOffsetUnit = 1;
    material.polygonOffsetFactor = 1;

    moveControl = new THREE.Mesh(geometry, material);
    moveControl.renderOrder = 9998;
    moveControl.position.set(point.x, point.y, point.z);
    moveControl.archMesh = archMesh;
    moveControl.tag = CONTROL_TAG;
    moveControl.defaultColor = color;
    moveControl.isMove = true;
    archMesh.add(moveControl);
    archMesh.moveControl = moveControl;
    return moveControl;
  }
}

function drawRotateControl(point, archMesh, color) {
  if (point) {
    let rotateControl = null;

    if (!color) {
      color = 0x0000ff;
    }
    color = new THREE.Color(color);

    const geometry = new THREE.TorusGeometry(4, 0.3, 16, 100);

    // if (radiansX) geometry.rotateX(radiansX);
    // if (radiansY) geometry.rotateY(radiansY);
    // if (radiansZ) geometry.rotateZ(radiansZ);

    const material = new THREE.MeshBasicMaterial({
      color: color,
      transparent: true,
      side: 2,
      // depthTest: false,
      // depthWrite: false,
      polygonOffset: true,
      opacity: 0.5,
    });
    material.polygonOffsetUnit = 1;
    material.polygonOffsetFactor = 1;

    rotateControl = new THREE.Mesh(geometry, material);
    rotateControl.renderOrder = 9998;
    rotateControl.position.set(point.x, point.y, point.z);
    rotateControl.archMesh = archMesh;
    rotateControl.tag = CONTROL_TAG;
    rotateControl.defaultColor = color;
    archMesh.add(rotateControl);
    archMesh.rotateControl = rotateControl;
    return rotateControl;
  }
}

function getSplinePoints(mainPoints, zvalue) {
  let twodPoints = [];
  for (const mpoint of mainPoints) {
    twodPoints.push(new THREE.Vector2(mpoint.x, mpoint.y));
  }
  // Create a sine-like wave
  const curve = new THREE.SplineCurve(twodPoints);
  const curvePoints = curve.getPoints(1000);

  let splinePoints = [];
  for (const point of curvePoints) {
    splinePoints.push(new THREE.Vector3(point.x, point.y, zvalue));
  }
  return splinePoints;
}

function drawArchCurve(points, existed, color) {
  if (!points || points.length < 2) {
    return;
  }
  if (!color) {
    color = 0x007bff;
  }

  let radius = 0.25;

  let tubeGeometry = existed
    ? existed.geometry
    : DynamicTubeGeometry(radius, 8, points.length * 2, false, false);
  tubeGeometry.update(radius, points);

  let line = existed;
  if (!existed) {
    let material = new THREE.MeshBasicMaterial({
      color: color, // color, //0xff9900,  0xff0000
      transparent: true,
      side: 2,
      // depthTest: false,
      // depthWrite: false,
      polygonOffset: true,
    }); //, opacity: 0.2, transparent: true });
    material.polygonOffsetUnit = 1;
    material.polygonOffsetFactor = 1;

    line = new THREE.Mesh(tubeGeometry, material);
    line.name = line.tag = "ArchCurve";
    // line.archPoints = points;
    line.radius = radius;
    line.renderOrder = 9997;
    line.visible = false;
    ioViewer.scene.add(line);
  }

  return line;
}

function drawArchPoint(point, archMesh, color) {
  if (point) {
    if (!color) {
      color = 0x00ff00;
    }
    let radius = 0.8;
    let material = new THREE.MeshPhongMaterial({
      color: color,
      flatShading: false,
      specular: 0x050505,
      shininess: 200,
      transparent: true,
      side: 2,
      // depthTest: false,
      // depthWrite: false,
      polygonOffset: true,
    });
    material.polygonOffsetUnit = 1;
    material.polygonOffsetFactor = 1;

    const pointMesh = new THREE.Mesh(
      new THREE.SphereBufferGeometry(radius),
      material,
    );
    pointMesh.renderOrder = 9998;
    pointMesh.position.set(point.x, point.y, point.z);
    pointMesh.current = point;
    pointMesh.archMesh = archMesh;
    pointMesh.isAdded = point.isAdded;
    pointMesh.defaultColor = color;

    archMesh.add(pointMesh);
    return pointMesh;
  }
}

function drawArchLine(pointMesh, centerX) {
  let color = 0xff0000; //0xff0000; 0x007bff;
  let radius = 0.2; //0xff0000;

  let point1 = pointMesh.position;

  let points = [];
  points.push(point1.x, point1.y, point1.z);
  points.push(centerX, point1.y, point1.z);

  const lineX = new MeshLine();
  lineX.setPoints(points);

  let line = pointMesh.line;
  if (!line) {
    const material = new MeshLineMaterial({
      color: color,
      lineWidth: radius,
      transparent: true,
      side: 2,
      // depthTest: false,
      // depthWrite: false,
      polygonOffset: true,
    });
    material.polygonOffsetUnit = 1;
    material.polygonOffsetFactor = 1;

    line = new THREE.Mesh(lineX.geometry, material);
    line.renderOrder = 9997;
    pointMesh.centerX = centerX;
    pointMesh.line = line;
  } else {
    let temp = line.geometry;
    line.geometry = lineX.geometry;
    temp.dispose();
  }
  return line;
}

function drawMidLine(point1, point2) {
  let color = 0xffd700; //0xff0000; 0x007bff;
  let radius = 0.25; //0xff0000;

  let points = [];
  points.push(point1.x, point1.y - 5, point1.z);
  points.push(point2.x, point2.y + 5, point2.z);

  const lineX = new MeshLine();
  lineX.setPoints(points);

  const material = new MeshLineMaterial({
    color: color,
    lineWidth: radius,
    transparent: true,
    side: 2,
    // depthTest: false,
    // depthWrite: false,
    polygonOffset: true,
  });
  material.polygonOffsetUnit = 1;
  material.polygonOffsetFactor = 1;
  // 0 -> no dash ; 1 -> alf dashline length ; 2 -> dashline === length
  material.dashArray = 0.02;
  // 0.5 -> balancing ; 0.1 -> more line : 0.9 -> more void
  material.dashRatio = 0.2;

  let line = new THREE.Mesh(lineX.geometry, material);
  line.renderOrder = 9997;
  return line;
}

function switchShowLines(value) {
  editContext.showLines = value;
  for (const model of allModels
    .filter((m) => m.measurePoints)
    .sort((a, b) => b.number - a.number)) {
    if (model.measureMeshes) {
      for (const mesh of model.measureMeshes) {
        mesh.visible =
          model.isUpper == viewMode.isUpper && editContext.showLines;
      }
    }
  }
}

function drawArchLabel(archLabel, text, point, color, archMesh) {
  // let archLabel = model.archLabel;
  if (!archLabel) {
    archLabel = document.createElement("div");
    archLabel.color = color;
    archLabel.point = point;
    archLabel.text = text;
    archLabel.style.color = `${color}`;
    archLabel.style.border = `2px solid ${color}`;
    archLabel.style.position = "absolute";
    // measureLabel.style.zIndex = "9999";
    archLabel.style.width = "auto";
    archLabel.style.height = "auto";
    archLabel.style.padding = "2px";
    archLabel.style.pointerEvents = "none";
    // measureLabel.style.opacity = "0.5";
    archLabel.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
    archLabel.style.display = "none";
    archLabel.archMesh = archMesh;
    document.getElementById("intraoralContainer").appendChild(archLabel);
  }

  if (!text) {
    text = archLabel.text;
  }
  archLabel.text = text;
  if (!point) {
    point = archLabel.point;
  }
  archLabel.point = point;
  if (!archMesh) {
    archMesh = archLabel.archMesh;
  }

  let canvasHalfWidth = ioViewer.renderer.domElement.offsetWidth / 2;
  let canvasHalfHeight = ioViewer.renderer.domElement.offsetHeight / 2;

  // let mesh = ioViewer.get_model_mesh(model.id);
  // let point = mesh.textPoint;

  let centerPosition = new THREE.Vector3(point.x, point.y, point.z);
  centerPosition.applyMatrix4(archMesh.matrixWorld);

  centerPosition = centerPosition.project(ioViewer.camera);
  centerPosition.x = centerPosition.x * canvasHalfWidth + canvasHalfWidth;
  centerPosition.y = -(centerPosition.y * canvasHalfHeight) + canvasHalfHeight;

  archLabel.textContent = text;
  // `${model.measureDistance.toFixed(2)} mm`;
  archLabel.style.left = `${centerPosition.x + 15}px`;
  archLabel.style.top = `${centerPosition.y - 15}px`;
  return archLabel;
}

function updateArcLabels() {
  let archMesh = viewMode.isUpper ? upperArchMesh : lowerArchMesh;
  if (editContext.isActive && archMesh && archMesh.labels) {
    for (const label of archMesh.labels) {
      drawArchLabel(label);
    }
  }
}

function updateSelectedPoints(clearSelection) {
  let archMesh = viewMode.isUpper ? upperArchMesh : lowerArchMesh;
  if (clearSelection) {
    archMesh.selectedPoints = [];
  }

  let selectedPoints = archMesh.selectedPoints;
  for (const point of archMesh.pointMeshes) {
    if (selectedPoints.includes(point)) {
      point.material.color = MEASURE_COLOR;
    } else {
      point.material.color = point.defaultColor;
    }
  }

  let count = selectedPoints.filter((p) => p).length;
  if (count == 2) {
    let p1 = selectedPoints[0].current;
    let p2 = selectedPoints[1].current;

    let middlePoint = new THREE.Vector3(
      (p1.x + p2.x) / 2,
      (p1.y + p2.y) / 2,
      (p1.z + p2.z) / 2,
    );
    updateSelectedDistance(archMesh);
    drawArchLabel(
      archMesh.labels[3],
      `${archMesh.selectedDistance} mm`,
      middlePoint,
    );
    archMesh.labels[3].visible = true;
    archMesh.labels[3].style.display = "block";
  }
}

function updateFirsSixArch() {
  if (lowerArchMesh && commonContext.lowerStart6 && commonContext.lowerEnd6) {
    let allPoints = lowerArchMesh.allPoints;
    let start = getNearestPoint2D(allPoints, commonContext.lowerStart6);
    let end = getNearestPoint2D(allPoints, commonContext.lowerEnd6);
    let startIndex = allPoints.indexOf(start);
    let endIndex = allPoints.indexOf(end);
    let tempPoints = allPoints.slice(
      Math.min(startIndex, endIndex),
      Math.max(startIndex, endIndex),
    );
    commonContext.lowerArchFirstSixSum = tempPoints.reduce(
      (acc, curr, i, arr) => {
        return i === 0 ? acc : acc + getDistance(arr[i - 1], arr[i]);
      },
      0,
    );
  }

  if (upperArchMesh && commonContext.upperStart6 && commonContext.upperEnd6) {
    let allPoints = upperArchMesh.allPoints;
    let start = getNearestPoint2D(allPoints, commonContext.upperStart6);
    let end = getNearestPoint2D(allPoints, commonContext.upperEnd6);
    let startIndex = allPoints.indexOf(start);
    let endIndex = allPoints.indexOf(end);
    let tempPoints = allPoints.slice(
      Math.min(startIndex, endIndex),
      Math.max(startIndex, endIndex),
    );
    commonContext.upperArchFirstSixSum = tempPoints.reduce(
      (acc, curr, i, arr) => {
        return i === 0 ? acc : acc + getDistance(arr[i - 1], arr[i]);
      },
      0,
    );
  }
}

//#endregion

//#region Get points

function getNearestPoint(points, target) {
  let minDistance = Infinity;
  let retval = points[0];
  for (const point of points) {
    let dist = getDistance(target, point);
    if (dist < minDistance) {
      minDistance = dist;
      retval = point;
    }
  }
  return retval;
}

function getNearestPoint2D(points, target) {
  let minDistance = Infinity;
  let retval = points[0];
  for (const point of points) {
    let dist = getDistance2D(target, point);
    if (dist < minDistance) {
      minDistance = dist;
      retval = point;
    }
  }
  return retval;
}

function getNearestPointByY(points, target, minX, maxX) {
  let minDistanceY = Infinity;
  let retval = points[0];
  for (const point of points) {
    let distY = Math.abs(target.y - point.y);
    if (distY < minDistanceY && point.x >= minX && point.x <= maxX) {
      minDistanceY = distY;
      retval = point;
    }
  }
  return retval;
}

function getCenterPointFromMeshes(mesh1, mesh2) {
  let minDistance = Infinity;
  let point1 = null;
  let point2 = null;
  mesh1.geometry.computeBoundingBox();
  mesh2.geometry.computeBoundingBox();
  let bb1 = offsetBox(mesh1.geometry.boundingBox);
  let bb2 = offsetBox(mesh2.geometry.boundingBox);
  let crossBox = mesh1.geometry.boundingBox
    .clone()
    .intersect(mesh2.geometry.boundingBox.clone());
  if (!crossBox.isEmpty()) {
    crossBox = offsetBox(crossBox);
    // drawBox(crossBox, 0xff0000, ioViewer.scen)
    return new THREE.Vector3(
      crossBox.center.x,
      crossBox.center.y,
      crossBox.center.z,
    );
  }

  point1 = bb1.center;
  point2 = bb2.center;
  // const pos1 = mesh1.geometry.getAttribute("position");
  // const pos2 = mesh2.geometry.getAttribute("position");
  // for (let i = 0; i < pos1.count; i++) {
  //   const x1 = pos1.getX(i);
  //   const y1 = pos1.getY(i);
  //   const z1 = pos1.getZ(i);
  //   for (let j = 0; j < pos2.count; i++) {
  //     const x2 = pos2.getX(i);
  //     const y2 = pos2.getY(i);
  //     const z2 = pos2.getZ(i);
  //     let dist = get3Distance(x1, y1, z1, x2, y2, z2);
  //     if (dist < minDistance) {
  //       minDistance = dist;
  //       point1 = new THREE.Vector3(x1, y1, z1);
  //       point2 = new THREE.Vector3(x2, y2, z2);
  //     }
  //   }
  // }
  if (point1 && point2) {
    return new THREE.Vector3(
      (point1.x + point2.x) / 2,
      (point1.y + point2.y) / 2,
      (point1.z + point2.z) / 2,
    );
  }
}

//#endregion

//#region Events

function archMouseClick(event) {}

function archMouseDown(event) {
  ioArchMenu.style.display = "none";
  const now = Date.now();
  const millis = now - clickTime;
  if (millis > clickDif && firstClick) {
    updateRaycasterFromEvent(event);

    if (event.button === 2) {
      clickedPoint = intersectDragPoint();
      newArchPoint = null;
      if (!clickedPoint) {
        newArchPoint = intersectArchMesh();
      }
      if (clickedPoint || newArchPoint) {
        ioArchMenu.style.display = "initial";
        ioArchMenu.style.left = event.offsetX + 1 + "px";
        ioArchMenu.style.top = event.offsetY + 1 + "px";

        ioArchAddBtn.style.display = newArchPoint ? "initial" : "none";
        ioArchRemoveBtn.style.display =
          clickedPoint && clickedPoint.isAdded ? "initial" : "none";
        ioArchMeasureBtn.style.display = clickedPoint ? "initial" : "none";
      }
    } else {
      dragPoint = intersectDragPoint();
      if (!dragPoint) {
        findArchTagControl();
      }
    }

    if (clickedPoint || dragPoint || tagPos || newArchPoint) {
      if (event.preventDefault) event.preventDefault();
      ioViewer.controls.enabled = false;
    }
  }
}

function archMouseMove(event) {
  updateRaycasterFromEvent(event);
  let newPoint = intersectArchPlane();
  if (!newPoint) return;
  if (dragPoint) {
    let archMesh = dragPoint.archMesh;
    newPoint = archMesh.worldToLocal(newPoint);
    newPoint.x -= dragPoint.dif.x;
    newPoint.y -= dragPoint.dif.y;
    let dx = newPoint.x - dragPoint.current.x;
    let dy = newPoint.y - dragPoint.current.y;
    let minDif = 0.05;
    if (Math.abs(dx) > minDif || Math.abs(dy) > minDif) {
      updateFromPointMesh(dragPoint, dx, dy);
      if (editContext.symmPoints && dragPoint.opposite) {
        let opMesh = dragPoint.opposite;
        let centerX = dragPoint.centerX;
        let cx = newPoint.x - centerX;
        let opPoint = new THREE.Vector3(centerX - cx, newPoint.y, 0);
        dx = opPoint.x - opMesh.current.x;
        dy = opPoint.y - opMesh.current.y;
        updateFromPointMesh(opMesh, dx, dy);
      }

      updateArchAndDistances(archMesh);
    }
  } else if (tagPos && tagControl) {
    let archMesh = tagControl.archMesh;
    if (tagControl.isMove) {
      newPoint.x -= tagControl.dif.x;
      newPoint.y -= tagControl.dif.y;
      archMesh.position.setX(newPoint.x);
      archMesh.position.setY(newPoint.y);
    } else {
      // const rotateCenter = tagControl.position.clone();
      let rotateCenter = new THREE.Vector3();
      tagControl.getWorldPosition(rotateCenter);

      const axis = new THREE.Vector3(0, 0, 1);
      const vectorBA = tagPos.clone().sub(rotateCenter).normalize();
      const vectorBC = newPoint.clone().sub(rotateCenter).normalize();
      const signedAngle = getSignedAngle(vectorBA, vectorBC, axis);
      // const angleRadians = vectorBA.angleTo(vectorBC);
      // const angleDegrees = angleRadians * (180 / Math.PI);
      archMesh.position.sub(rotateCenter); // remove the offset
      archMesh.position.applyAxisAngle(axis, signedAngle); // rotate the POSITION
      archMesh.position.add(rotateCenter); // re-add the offset
      archMesh.rotateOnWorldAxis(axis, signedAngle);
    }
    tagPos = newPoint;
  } else {
    findArchTagControl(true);
  }
}

function updateArchAndDistances(archMesh) {
  archMesh.allPoints = getSplinePoints(archMesh.mainPoints, archMesh.centerZ);
  drawArchCurve(archMesh.allPoints, archMesh);

  let totalDistance = archMesh.allPoints.reduce((acc, curr, i, arr) => {
    return i === 0 ? acc : acc + getDistance(arr[i - 1], arr[i]);
  }, 0);
  archMesh.totalDistance = totalDistance.toFixed(2);

  drawArchLabel(archMesh.labels[0], `${archMesh.totalDistance} mm`);

  updatePremolarDistance(archMesh);
  drawArchLabel(archMesh.labels[1], `${archMesh.premolarDistance} mm`);

  // updateFirstSixDistance(archMesh);
  // drawArchLabel(archMesh.labels[2], `${archMesh.firstSixDistance} mm`);

  updateSelectedDistance(archMesh);
  if (archMesh.selectedDistance)
    drawArchLabel(archMesh.labels[3], `${archMesh.selectedDistance} mm`);
  else {
    archMesh.labels[3].style.display = "none";
  }

  if (viewMode.isUpper && upperArchMesh) {
    commonContext.upperArchAllSum = +upperArchMesh.totalDistance;
    commonContext.upperArchPremolarSum = +upperArchMesh.premolarDistance;
    // commonContext.upperArchFirstSixSum = +upperArchMesh.firstSixDistance;
  } else if (lowerArchMesh) {
    commonContext.lowerArchAllSum = +lowerArchMesh.totalDistance;
    commonContext.lowerArchPremolarSum = +lowerArchMesh.premolarDistance;
    // commonContext.lowerArchFirstSixSum = +lowerArchMesh.firstSixDistance;
  }

  updateFirsSixArch();
}

function getSignedAngle(v1, v2, referenceAxis) {
  // Calculate the unsigned angle between v1 and v2
  const angle = v1.angleTo(v2); // result is always positive, in radians [0, PI]

  // Calculate the normal of the plane defined by v1 and v2 using the cross product
  const crossProduct = new THREE.Vector3().crossVectors(v1, v2);

  // Determine the sign by checking the direction of the cross product relative to the reference axis
  // A positive dot product means they are generally in the same direction
  const sign = Math.sign(crossProduct.dot(referenceAxis));

  // The final signed angle
  return angle * sign;
}

function updateFromPointMesh(pointMesh, dx, dy) {
  let newX = pointMesh.current.x + dx;
  let newY = pointMesh.current.y + dy;
  pointMesh.position.setX(newX);
  pointMesh.current.setX(newX);
  pointMesh.position.setY(newY);
  pointMesh.current.setY(newY);

  if (pointMesh.line) {
    drawArchLine(pointMesh, pointMesh.centerX);
  }
  return;

  //#region old logic
  let points = pointMesh.prevPoints;
  if (points) {
    let length = points.length;
    for (const point of points) {
      let index = points.indexOf(point);
      let weight = index / length;
      let pdx = dx * weight;
      let pdy = dy * weight;
      point.setX(point.x + pdx);
      point.setY(point.y + pdy);
    }
  }
  points = pointMesh.nextPoints;
  if (points) {
    let length = points.length;
    for (const point of points) {
      let index = points.indexOf(point);
      let weight = (length - index) / length;
      let pdx = dx * weight;
      let pdy = dy * weight;
      point.setX(point.x + pdx);
      point.setY(point.y + pdy);
    }
  }
  //#endregion
}

function archMouseUp(event) {
  console.log("measureMouseUp");
  dragPoint = tagPos = null;
  ioViewer.controls.enabled = true;
  updateArcLabels();
}

//#endregion

//#region Intersect

function intersectDragPoint() {
  let archMesh = viewMode.isUpper ? upperArchMesh : lowerArchMesh;
  if (!archMesh) return null;

  let retval = null;
  let pointMeshes = archMesh.pointMeshes;
  let intersects = ioRaycaster.intersectObjects(pointMeshes);
  let intersected = intersects.length > 0 ? intersects[0] : null;
  if (intersected) {
    retval = intersected.object;
    let archMesh = retval.archMesh;
    let intersectPoint = intersected.point;
    intersectPoint = archMesh.worldToLocal(intersectPoint);
    retval.dif = new THREE.Vector3(
      intersectPoint.x - retval.position.x,
      intersectPoint.y - retval.position.y,
      0,
    );
  }
  return retval;
}

function intersectArchPlane() {
  let point = null;
  let intersects = ioRaycaster.intersectObject(archPlane);
  if (intersects.length > 0) {
    point = intersects[0].point;
  }
  return point;
}

function findArchTagControl(isHighlight) {
  let archMesh = viewMode.isUpper ? upperArchMesh : lowerArchMesh;
  if (!archMesh) return null;

  let retval = null;
  let taggedMeshes = [];
  archMesh.children.forEach((mesh) => {
    if (mesh.tag == CONTROL_TAG) {
      taggedMeshes.push(mesh);
    }
  });
  let intersects = ioRaycaster.intersectObjects(taggedMeshes);
  let intersected = intersects.length > 0 ? intersects[0] : null;
  if (intersected) {
    retval = intersected.object;
    if (tagControl != retval && isHighlight) {
      retval.material.color = YELLOW_COLOR;
      document.body.style.cursor = "pointer";
    }
    tagControl = retval;
    if (!isHighlight) {
      tagControl.renderOrder = 9999;
      tagPos = intersectArchPlane(); //intersected.point;
      let archMesh = retval.archMesh;
      retval.dif = new THREE.Vector3(
        tagPos.x - archMesh.position.x,
        tagPos.y - archMesh.position.y,
        0,
      );
    }
  } else if (tagControl) {
    tagControl.renderOrder = 9998;
    tagControl.material.color = tagControl.defaultColor;
    tagControl = null;
    document.body.style.cursor = "default";
  }

  return retval;
}

function intersectArchMesh() {
  let archMesh = viewMode.isUpper ? upperArchMesh : lowerArchMesh;
  let point = null;
  let intersects = ioRaycaster.intersectObject(archMesh);
  if (intersects.length > 0) {
    point = intersects[0].point;
  }
  return point;
}

//#endregion

//#region Bezier

const bezierTension = 0.45;

function getBezierPoints(mainPoints, zvalue) {
  let bezierPoints = [];
  let twodPoints = [];

  let pts = [];
  mainPoints.forEach((konvaPoint) => {
    pts.push(konvaPoint.x, konvaPoint.y);
  });

  let cp = [];
  let n = pts.length;
  pts.push(pts[0], pts[1], pts[2], pts[3]);
  pts.unshift(pts[n - 1]);
  pts.unshift(pts[n - 1]);
  for (var i = 0; i < n; i += 2) {
    cp = cp.concat(
      getControlPoints(
        pts[i],
        pts[i + 1],
        pts[i + 2],
        pts[i + 3],
        pts[i + 4],
        pts[i + 5],
        bezierTension,
      ),
    );
  }
  cp = cp.concat(cp[0], cp[1]);
  for (let i = 2, j = 0; i < n + 2; i += 2, j++) {
    let kp = mainPoints[j];
    kp.p0 = { x: pts[i], y: pts[i + 1] };
    kp.p1 = { x: cp[2 * i - 2], y: cp[2 * i - 1] };
    kp.p2 = { x: cp[2 * i], y: cp[2 * i + 1] };
    kp.p3 = { x: pts[i + 2], y: pts[i + 3] };

    let kpNext = mainPoints[j + 1];
    if (!kpNext) {
      break;
      kpNext = mainPoints[0];
    }

    let width = Math.abs(kp.x - kpNext.x);
    let height = Math.abs(kp.y - kpNext.y);
    let max = Math.max(width, height);
    let tempPoints = getSplitPoints(kp, max * 2);
    twodPoints.push(...tempPoints);
  }
  for (const point of twodPoints) {
    bezierPoints.push(new THREE.Vector3(point.x, point.y, zvalue));
  }
  return bezierPoints;
}

function getControlPoints(x0, y0, x1, y1, x2, y2, t) {
  var d01 = Math.sqrt(Math.pow(x1 - x0, 2) + Math.pow(y1 - y0, 2));
  var d12 = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));

  var fa = (t * d01) / (d01 + d12);
  var fb = t - fa;

  var p1x = x1 + fa * (x0 - x2);
  var p1y = y1 + fa * (y0 - y2);

  var p2x = x1 - fb * (x0 - x2);
  var p2y = y1 - fb * (y0 - y2);

  return [p1x, p1y, p2x, p2y];
}

function getSplitPoints(kp, splitCount) {
  const splitPoints = [
    new THREE.Vector2(kp.x, kp.y), // { x: kp.x, y: kp.y }
  ];
  for (let i = 1; i < splitCount; i++) {
    let splitPoint = splitBezierCurve(
      kp.p0,
      kp.p1,
      kp.p2,
      kp.p3,
      i / splitCount,
    );
    splitPoints.push(splitPoint);
  }
  return splitPoints;
}

function splitBezierCurve(p0, p1, p2, p3, proportion) {
  function lerp(a, b, t) {
    var s = 1 - t;
    return { x: a.x * s + b.x * t, y: a.y * s + b.y * t };
  }

  var p4 = lerp(p0, p1, proportion);
  var p5 = lerp(p1, p2, proportion);
  var p6 = lerp(p2, p3, proportion);
  var p7 = lerp(p4, p5, proportion);
  var p8 = lerp(p5, p6, proportion);
  var p9 = lerp(p7, p8, proportion);

  return new THREE.Vector2(p9.x, p9.y); //p9;
}

//#endregion

//#region Report

async function exportReport() {
  console.log("exportReportClick");
  showLoader(`Preparing data`, `Please wait...`, true);
  prepareIoContainer();

  let tempUpper = viewMode.isUpper;
  let tempLower = viewMode.isLower;

  if (!autoTableInitialised) {
    applyPlugin(jsPDF);
    autoTableInitialised = true;
  }

  if (!reportZoom) {
    await delay(100);
    let padding = -80;
    let w = (defaultIoBox.size.x + padding) / 2;
    let h = (defaultIoBox.size.y + padding) / 2;
    let fovX = ioViewer.camera.fov * ioViewer.camera.aspect;
    let fovY = ioViewer.camera.fov;
    let distanceX = w / Math.tan((Math.PI * fovX) / 360) + w;
    let distanceY = h / Math.tan((Math.PI * fovY) / 360) + w;
    reportZoom = Math.max(distanceX, distanceY);
    console.log("reportZoom", reportZoom);
  }

  const pdf = new jsPDF();
  const pdfWidth = pdf.internal.pageSize.getWidth(); // 210
  const pdfHeight = pdf.internal.pageSize.getHeight(); // 297
  const headerText = "Arch&Teeth Measurement";

  const fontSize = 14;
  const textMargin = 4;

  pdf.setFontSize(fontSize + 6);
  pdf.setFont("helvetica");
  let lineHeightFactor = 0.7;

  let textDims = pdf.getTextDimensions(headerText);
  let headerLeft = pdfWidth / 2 - textDims.w / 2;
  let headerTop = 12; //pdfHeight / 2 - textDims.h;
  pdf.text(headerText, headerLeft, headerTop);
  pdf.setFontSize(fontSize);
  let textTop = headerTop + textDims.h;

  let patientName = `${vueApp.patientFirstName} ${vueApp.patientLastName}`;
  textDims = pdf.getTextDimensions(patientName);
  let textWidth = textDims.w;
  let textLeft = pdfWidth / 2 - textWidth / 2;
  pdf.text(patientName, textLeft, textTop);
  textTop += textDims.h * 2;

  lineHeightFactor = pdf.getLineHeightFactor();
  console.log("lineHeightFactor", lineHeightFactor);
  let tableWidth = pdfWidth / 2 - textMargin * 2;
  let imgWitdh = tableWidth;
  let imgHeight = tableWidth * 0.7;

  if (commonContext.hasUpper) {
    let tableTop = textTop;
    let tableLeft = textMargin * 1.5;
    pdf.setFontSize(12);
    pdf.text("Crowding Upper", tableLeft + 2, tableTop);
    textDims = pdf.getTextDimensions(patientName);
    tableTop += textDims.h * 0.7;
    pdf.setFontSize(9);
    pdf.autoTable({
      startY: tableTop,
      margin: { left: tableLeft },
      tableWidth: tableWidth,
      styles: {
        halign: "left",
        lineWidth: 0.2,
        lineColor: [0, 0, 0],
        fontSize: 8,
        fillColor: false,
        textColor: [0, 0, 0],
      },
      alternateRowStyles: { fillColor: false },
      body: [
        [``, `Teeth Sum (mm)`, `Arch (mm)`, `Delta (mm)`],
        [
          "3 to 3",
          `${commonContext.upperTeethFirstSixSum.toFixed(2)}`,
          `${commonContext.upperArchFirstSixSum.toFixed(2)}`,
          `${commonContext.getUpperFirstSixDeltaText()}`,
        ],
        [
          "5 to 5",
          `${commonContext.upperTeethPremolarSum.toFixed(2)}`,
          `${commonContext.upperArchPremolarSum.toFixed(2)}`,
          `${commonContext.getUpperPremolarDeltaText()}`,
        ],
        [
          "All Teeth",
          `${commonContext.upperTeethAllSum.toFixed(2)}`,
          `${commonContext.upperArchAllSum.toFixed(2)}`,
          `${commonContext.getUpperAllDeltaText()}`,
        ],
      ],
    });
    await resetIoView();
    viewMode.isUpper = true;
    viewMode.isLower = false;
    await switchViewModes(true, false);

    let imgTop = pdf.lastAutoTable.finalY + textMargin;
    // imgTop += imgSize + textMargin / 2;
    let imgLeft = tableLeft;
    let imgData = await getIntraoralScreenshot();
    pdf.addImage(imgData, "JPEG", imgLeft, imgTop, imgWitdh, imgHeight);

    await enterArchMode();
    for (const label of upperArchMesh.labels) {
      // label.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
      label.style.backgroundColor = "#ffffff";
      label.style.color = `rgba(0, 0, 0, 1)`;
    }

    let reportMeshes = [upperArchMesh.moveControl, upperArchMesh.rotateControl]
      .concat(upperArchMesh.pointMeshes.map((p) => p.line))
      .filter((m) => m);
    for (const mesh of reportMeshes) {
      mesh.visible = false;
    }
    imgTop += imgHeight + textMargin;
    imgData = await getIntraoralScreenshot();
    pdf.addImage(imgData, "JPEG", imgLeft, imgTop, imgWitdh, imgHeight);
    for (const label of upperArchMesh.labels) {
      label.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
      label.style.color = label.color;
    }
    for (const mesh of reportMeshes) {
      mesh.visible = true;
    }
    await leaveArchMode();

    await enterMeasureMode();
    let measureModels = allModels
      .filter((m) => m.measureLabel)
      .concat(molarLines);
    for (const model of measureModels) {
      let label = model.measureLabel;
      label.style.backgroundColor = "#ffffff";
      label.style.color = `rgba(0, 0, 0, 1)`;
    }
    imgTop += imgHeight + textMargin;
    imgData = await getIntraoralScreenshot();
    pdf.addImage(imgData, "JPEG", imgLeft, imgTop, imgWitdh, imgHeight);
    for (const model of measureModels) {
      let label = model.measureLabel;
      label.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
      label.style.color = label.color;
    }
  }

  if (commonContext.hasLower) {
    let tableTop = textTop;
    let tableLeft = pdfWidth / 2 + textMargin * 0.5;
    pdf.setFontSize(12);
    pdf.text("Crowding Lower", tableLeft + 2, tableTop);
    textDims = pdf.getTextDimensions(patientName);
    tableTop += textDims.h * 0.7;
    pdf.autoTable({
      startY: tableTop,
      margin: { left: tableLeft },
      styles: {
        halign: "left",
        lineWidth: 0.2,
        lineColor: [0, 0, 0],
        fontSize: 8,
        fillColor: false,
        textColor: [0, 0, 0],
      },
      tableWidth: tableWidth,
      alternateRowStyles: { fillColor: false },
      body: [
        [``, `Teeth Sum  (mm)`, `Arch (mm)`, `Delta (mm)`],
        [
          "3 to 3",
          `${commonContext.lowerTeethFirstSixSum.toFixed(2)}`,
          `${commonContext.lowerArchFirstSixSum.toFixed(2)}`,
          `${commonContext.getLowerFirstSixDeltaText()}`,
        ],
        [
          "5 to 5",
          `${commonContext.lowerTeethPremolarSum.toFixed(2)}`,
          `${commonContext.lowerArchPremolarSum.toFixed(2)}`,
          `${commonContext.getLowerPremolarDeltaText()}`,
        ],
        [
          "All Teeth",
          `${commonContext.lowerTeethAllSum.toFixed(2)}`,
          `${commonContext.lowerArchAllSum.toFixed(2)}`,
          `${commonContext.getLowerAllDeltaText()}`,
        ],
      ],
    });

    await resetIoView();
    viewMode.isUpper = false;
    viewMode.isLower = true;
    await switchViewModes(false, true);

    let imgTop = pdf.lastAutoTable.finalY + textMargin;
    // imgTop += imgSize + textMargin / 2;
    let imgLeft = tableLeft;
    let imgData = await getIntraoralScreenshot();
    pdf.addImage(imgData, "JPEG", imgLeft, imgTop, imgWitdh, imgHeight);

    await enterArchMode();
    for (const label of lowerArchMesh.labels) {
      // label.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
      label.style.backgroundColor = "#ffffff";
      label.style.color = `rgba(0, 0, 0, 1)`;
    }
    let reportMeshes = [lowerArchMesh.moveControl, lowerArchMesh.rotateControl]
      .concat(lowerArchMesh.pointMeshes.map((p) => p.line))
      .filter((m) => m);
    for (const mesh of reportMeshes) {
      mesh.visible = false;
    }
    imgTop += imgHeight + textMargin;
    imgData = await getIntraoralScreenshot();
    pdf.addImage(imgData, "JPEG", imgLeft, imgTop, imgWitdh, imgHeight);
    await leaveArchMode();
    for (const label of lowerArchMesh.labels) {
      label.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
      label.style.color = label.color;
    }
    for (const mesh of reportMeshes) {
      mesh.visible = true;
    }
    await enterMeasureMode();
    let measureModels = allModels
      .filter((m) => m.measureLabel)
      .concat(molarLines);
    for (const model of measureModels) {
      let label = model.measureLabel;
      label.style.backgroundColor = "#ffffff";
      label.style.color = `rgba(0, 0, 0, 1)`;
    }
    imgTop += imgHeight + textMargin;
    imgData = await getIntraoralScreenshot();
    pdf.addImage(imgData, "JPEG", imgLeft, imgTop, imgWitdh, imgHeight);
    for (const model of measureModels) {
      let label = model.measureLabel;
      label.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
      label.style.color = label.color;
    }
  }

  if (commonContext.hasLower && commonContext.hasUpper) {
    pdf.addPage();

    pdf.setFontSize(fontSize);
    let patientName = `Bolton Analysis`;
    textDims = pdf.getTextDimensions(patientName);
    let textWidth = textDims.w;
    let textLeft = pdfWidth / 2 - textWidth / 2;
    pdf.text(patientName, textLeft, headerTop);
    headerTop += textDims.h;

    let tableTop = headerTop;
    let tableLeft = textMargin * 1.5;
    let tableWidth = pdfWidth - textMargin * 3;
    pdf.autoTable({
      startY: tableTop,
      margin: { left: tableLeft },
      tableWidth: tableWidth,
      styles: {
        halign: "left",
        lineWidth: 0.2,
        lineColor: [0, 0, 0],
        fontSize: 10,
        fillColor: false,
        textColor: [0, 0, 0],
      },
      alternateRowStyles: { fillColor: false },
      body: [
        [``, `Lower (mm)`, `Upper (mm)`, `Ratio (%)`, "Bolton Analysis"],
        [
          "3 to 3",
          `${commonContext.lowerTeethFirstSixSum.toFixed(2)}`,
          `${commonContext.upperTeethFirstSixSum.toFixed(2)}`,
          `${commonContext.getFirstSixRatioPercent()}`,
          `${commonContext.getFirstSixBoltonAnalysis()}`,
        ],
        [
          "6 to 6",
          `${commonContext.lowerTeethTwelveSum.toFixed(2)}`,
          `${commonContext.upperTeethTwelveSum.toFixed(2)}`,
          `${commonContext.getFirstTwelveRatioPercent()}`,
          `${commonContext.getFirstTwelveBoltonAnalysis()}`,
          ,
        ],
      ],
    });
  }

  let fullName = `${headerText} ${vueApp.patientFirstName}_${
    vueApp.patientLastName
  }_${getFormattedDate(true)}.pdf`;

  pdf.save(fullName);

  restoreIoContainer();

  await resetIoView();
  viewMode.isUpper = tempUpper;
  viewMode.isLower = tempLower;
  await switchViewModes(tempUpper, tempLower);
  await switchArchMode(true);

  hideLoader();
}

function prepareIoContainer() {
  let ioContainer = document.getElementById("intraoralContainer");
  ioContainer.style.left = "-9999px";
  ioContainer.style.top = "-9999px";
  // ioContainer.setAttribute("hidden", "hidden");
  ioContainer.style.width = "1000px";
  ioContainer.style.height = "700px";
  ioViewer.do_resize();
  // ioViewer.scene.background = new THREE.Color("#FFFFFF");
  ioViewer.camera.updateProjectionMatrix();
  ioViewer.controls.update();
}

function restoreIoContainer() {
  let ioContainer = document.getElementById("intraoralContainer");
  ioViewer.scene.background = new THREE.Color(sceneBackgroundColor);
  ioContainer.style.width = "100%";
  ioContainer.style.height = "100%";
  ioContainer.style.left = "0";
  ioContainer.style.top = "0";
  ioViewer.do_resize();
}

async function getIntraoralScreenshot() {
  rotateIntraoralToView(2);
  ioViewer.camera.position.z = reportZoom; //defaultIoZoom / 8;
  await delay(100);
  let ioContainer = document.getElementById("intraoralContainer");
  let imgCanvas = await html2canvas(ioContainer, {
    scale: 2, // Higher scale for better quality
    useCORS: true,
    allowTaint: true,
    logging: false,
  });
  let img = imgCanvas.toDataURL("image/jpeg");
  return img;

  let canvas = ioViewer.renderer.domElement;
  ioViewer.camera.position.z = 0; //defaultIoZoom / 8;
  canvas.setAttribute("hidden", "hidden");
  // ioViewer.scene.background = new THREE.Color("#FFFFFF");

  // rotateByAngle(-90, 0.75);
  setSquareView();
  await delay(100);
  let imgData = getImgDataForCurrentView(canvas);
  ioViewer.do_resize();

  //

  return imgData;
}

function rotateByAngle(angle, zoomPercent) {
  if (!zoomPercent) zoomPercent = 1;
  vueApp.lookAtPoint({ x: 0, y: 0, z: 0 });
  resetCameraUp();
  ioViewer.camera.position.x = 0;
  ioViewer.camera.position.y = 0;
  ioViewer.camera.position.z = defaultZoom * zoomPercent;
  ioViewer.camera.position.applyQuaternion(
    new THREE.Quaternion().setFromAxisAngle(
      new THREE.Vector3(0, 1, 0), // The positive y-axis
      degreesToRadians(angle), // The amount of rotation to apply this time
    ),
  );

  ioViewer.camera.updateProjectionMatrix();
  ioViewer.controls.update();
}

function setSquareView() {
  let width = 1000; // : 1920; 1280
  let height = 700; //: 1080; 720
  // let rect = ioViewer.parent_element.getBoundingClientRect();
  // let size = Math.min(rect.height, rect.width);
  ioViewer.renderer.setSize(width, height);
  ioViewer.camera.aspect = width / height;
  ioViewer.camera.updateProjectionMatrix();
}

//#endregion

export {
  initializeIoArch,
  switchArchMode,
  enterArchMode,
  leaveArchMode,
  archMouseClick,
  archMouseMove,
  archMouseUp,
  archMouseDown,
  switchShowLines,
  exportReport,
  // measureMouseClick,
  // measureMouseMove,
  // measureMouseUp,
  // measureMouseDown,
  commonContext,
};
